import React, { useCallback, useEffect, useState } from 'react';
import PageTitle from '../../components/PageTitle';
import { useIntl } from 'react-intl';
import { showFormErrors } from '../../actions/login-actions';
import LoginForm from './components/LoginForm';
import SocialLoginForm from '../../components/SocialLoginForm';
import Whitebox from '../../components/WhiteboxPage';
import { LoginFieldError } from '../../types';
import { LoginPageMessages } from './messages';
import { useAppDispatch } from '../../stores/redux.store';
import { useMicrobranding } from '../../utilities/hooks/useMicrobranding';
import Footer from './components/Footer';
import Header from './components/Header';
import MaybeSocialLoginPanel from './components/MaybeSocialLoginPanel';
import WhiteboxFooter from './components/WhiteboxFooter';
import { loginPageId } from './constants';
import { addBreadcrumb } from '../../utilities/analytics/error-reporting';
import { isFedM } from '../../utilities/env';
import { useCobrandingApplication, useEmailFromSearchParams } from './hooks/utility';
import { useLogin } from './hooks/useLogin';
import { useLoginSubmission } from './hooks/useLoginSubmission';
import { useSocialLogin } from './hooks/useSocialLogin';
import { useGoogleOneTap } from './hooks/useGoogleOneTap';
import RememberedAccountsPage from './remembered-accounts/page';
import { fetchPageState } from '../../utilities/fetch-api';
import MaybePasskeyLoginPanel from './components/Passkey';
import { usePasskeyLogin } from './hooks/usePasskeyLogin';
import { useOrgLoginConfig } from './hooks/useOrgLoginConfig';

export async function loginLoader({ request }: { request: Request }) {
  return await fetchPageState('/rest/login/state', request, {
    capabilities: ['capability-group--login'],
  });
}

const LoginPage = () => {
  const { oidcContext, isEmbedded, isMobileApp, screenSize } = useMicrobranding();

  const dispatch = useAppDispatch();

  const {
    isPasswordlessAllowed,
    checkUsername,
    isUsernameChecked,
    checkUsernameError,
    error: loginError,
    redirectType,
    isRestrictedEmail,
    isCheckUsernameLoading,
    resetLogin,
    infoCode,
    rememberMeEnabled,
    isRememberMeChecked,
    onToggleRememberMeCheckbox,
  } = useLogin();

  const {
    isLoading,
    onLogin: handleLogIn,
    onPasswordlessLogin: handlePasswordlessLogIn,
  } = useLoginSubmission();

  const {
    onAppleLogin,
    onSlackLogin,
    onMicrosoftLogin,
    onGoogleSocialLogin,
    onDismissGoogleOneTap,
    socialLoginEligibility,
    envSupportsSocialLogin,
    isSocialBlocked,
  } = useSocialLogin();

  const { onPasskeyLogin, authenticating } = usePasskeyLogin();

  const { redirectToSingleSignOn } = useOrgLoginConfig();

  const cobrandingApplication = useCobrandingApplication();

  const isLoginSecondStep = isUsernameChecked;

  const { formatMessage } = useIntl();

  const emailFromSearchParams = useEmailFromSearchParams();

  const [email, setEmail] = useState(() => emailFromSearchParams || '');

  // Sync state with query params
  // In the case when the query params change after the component is first rendered
  useEffect(() => {
    if (emailFromSearchParams) {
      setEmail(emailFromSearchParams);
    }
  }, [emailFromSearchParams]);

  useGoogleOneTap({ email });

  const { highlightedSocialProvider = null, isEligibleForSocialLoginOnly = null } =
    socialLoginEligibility || {};

  const showSocialLoginOnly =
    isLoginSecondStep &&
    isEligibleForSocialLoginOnly &&
    highlightedSocialProvider &&
    envSupportsSocialLogin;

  const handleGoogleSocialLogin = useCallback(() => {
    if (envSupportsSocialLogin) {
      onGoogleSocialLogin(email);
    }
  }, [email, onGoogleSocialLogin, envSupportsSocialLogin]);

  const handleMicrosoftLogin = useCallback(() => {
    if (envSupportsSocialLogin) {
      onMicrosoftLogin(email);
    }
  }, [email, onMicrosoftLogin, envSupportsSocialLogin]);

  const handleAppleLogin = useCallback(() => {
    if (envSupportsSocialLogin) {
      onAppleLogin(email);
    }
  }, [email, onAppleLogin, envSupportsSocialLogin]);

  const handleSlackLogin = useCallback(() => {
    if (envSupportsSocialLogin) {
      onSlackLogin(email);
    }
  }, [email, onSlackLogin, envSupportsSocialLogin]);

  const handlePasskeyLogin = useCallback(() => {
    onPasskeyLogin();
  }, [onPasskeyLogin]);

  const handleValidationError = useCallback(
    (errors: LoginFieldError) => dispatch(showFormErrors(errors)),
    [dispatch]
  );

  const handleContinue = (email: string) => {
    checkUsername(email);
  };

  const handleFormClick = useCallback(() => {
    if (envSupportsSocialLogin && screenSize === 'mobile') {
      onDismissGoogleOneTap();
    }
  }, [onDismissGoogleOneTap, screenSize, envSupportsSocialLogin]);

  const handleEmailChange = useCallback((email: string) => {
    setEmail(email);
    if (isFedM()) {
      addBreadcrumb({
        level: 'debug',
        message: 'FedRAMP login email input change',
        category: 'ui',
        data: {
          emailLength: email.length,
          emailType: typeof email,
          emailConstructor: email && email.constructor ? email.constructor.name : undefined,
        },
      });
    }
  }, []);

  return (
    <>
      <PageTitle title={formatMessage(LoginPageMessages.headTitle)} />
      <Whitebox
        pageId={loginPageId}
        header={
          <Header
            highlightedSocialProvider={highlightedSocialProvider}
            showSocialLoginOnly={showSocialLoginOnly}
          />
        }
        footer={<Footer oidcContext={oidcContext} isEmbedded={isEmbedded} />}
        whiteboxFooter={
          <WhiteboxFooter
            showSocialLoginOnly={showSocialLoginOnly}
            isMobileApp={isMobileApp}
            oidcContext={oidcContext}
          />
        }>
        {showSocialLoginOnly ? (
          <SocialLoginForm
            email={email}
            highlightedSocialProvider={highlightedSocialProvider}
            handleGoogleLogin={handleGoogleSocialLogin}
            handleMicrosoftLogin={handleMicrosoftLogin}
            handleAppleLogin={handleAppleLogin}
            handleSlackLogin={handleSlackLogin}
          />
        ) : (
          <>
            <LoginForm
              application={cobrandingApplication || null}
              redirectType={!isLoginSecondStep ? redirectType : null}
              isRedirecting={redirectType !== null}
              isSecondStep={isLoginSecondStep}
              isPasswordlessAllowed={isPasswordlessAllowed}
              isLoading={isLoading}
              isCheckUsernameLoading={isCheckUsernameLoading}
              onContinue={handleContinue}
              onLogIn={handleLogIn}
              onPasswordlessLogIn={handlePasswordlessLogIn}
              error={loginError}
              info={infoCode}
              fieldErrors={{
                email: checkUsernameError,
              }}
              email={email}
              onValidationError={handleValidationError}
              onEditEmail={resetLogin}
              onFormClick={handleFormClick}
              restrictedEmail={isRestrictedEmail}
              pageId={loginPageId}
              onEmailChange={handleEmailChange}
              showRememberMeCheckbox={rememberMeEnabled}
              rememberMeChecked={isRememberMeChecked}
              onToggleRememberMeCheckbox={onToggleRememberMeCheckbox}
              onRedirectToSingleSignOn={redirectToSingleSignOn}
            />

            <MaybePasskeyLoginPanel
              onPasskeyLogin={handlePasskeyLogin}
              authenticating={authenticating}
            />

            <MaybeSocialLoginPanel
              isEmbedded={isEmbedded}
              envSupportsSocialLogin={envSupportsSocialLogin}
              isSocialBlocked={isSocialBlocked}
              onSlackLogin={handleSlackLogin}
              onAppleLogin={handleAppleLogin}
              onMicrosoftLogin={handleMicrosoftLogin}
              onGoogleLogin={handleGoogleSocialLogin}
            />
          </>
        )}
      </Whitebox>
    </>
  );
};

function LoginRoute() {
  // TODO: KIRBY-8427: tidy up this double check.
  const { shouldShowRememberedAccounts } = useLogin();
  if (shouldShowRememberedAccounts) {
    return <RememberedAccountsPage />;
  }
  return <LoginPage />;
}

export default LoginRoute;
